import * as React from 'react';

import cx from 'classnames';
import invariant from 'invariant';
import { keyBy } from 'lodash';

import { IconName } from 'components/Icon';
import Table from 'components/Table';
import TableRow from 'components/TableRow';
import Text from 'components/Text';

import getField, { Field } from './ComposableTableField';
import { getHeader } from './generators/Header';

import './styles/ComposableTable.scss';

type ColumnHeaderIcon = {
  iconName: IconName;
  tooltip: string;
};

export type ColumnHeader = {
  icon?: ColumnHeaderIcon;
  name: string;
  sortValue?: string;
  type?: 'avatar';
  // `width` of cell content not including sort icons, tooltipped icons, or cell padding
  width?: number;
};

export type ColumnGeneratorFunc<T> = (row: T) => any;
export type Props<T> = {
  className?: string;
  columnHeaders?: ColumnHeader[];
  columnGenerators: ColumnGeneratorFunc<T>[];
  rows: T[];
  keysOfActiveRows?: any[];
  keyField: Field<T>;
  actionsColumn?: boolean;
  handleSort?: Function;
  handleRowClick?: (arg0: T) => void;
  loading?: boolean;
  fixedHeader?: boolean;
  topOffset?: number;
  // `spreadsheet` adds column letters and row numbers
  // so that the table looks like a spreadsheet
  spreadsheet?: boolean;
};

export type SortDirection = 'asc' | 'desc';

type State = {
  sortColumn?: string;
  sortDirection?: SortDirection;
};

// anything that Composable table needs to know about rows specifically,
// should go in here into this interface.
export interface GenericRowInterface {
  onClick?: () => void;
}
class ComposableTable<T extends (GenericRowInterface | {})> extends React.Component<Props<T>, State> {
  state: State = {
    sortColumn: undefined,
    sortDirection: undefined,
  };

  generateHeaders = (columnHeaders: ColumnHeader[]): Array<React.ReactNode> => {
    const { sortColumn, sortDirection } = this.state;
    const { topOffset, spreadsheet } = this.props;

    const generatedHeaders = columnHeaders.map((header, index) => {
      const sortProps = header.sortValue
        ? {
            onClick: this.handleSort,
            sortColumn,
            sortDirection,
          }
        : undefined;

      return getHeader(header, index, sortProps, topOffset, spreadsheet);
    });

    if (this.props.spreadsheet) {
      // add an empty column header to accommodate the row numbers
      const emptyColumnHeader = (
        <th key="line-numbers" className="ComposableTableHeader" style={{ width: 20 }} />
      );
      return [emptyColumnHeader, ...generatedHeaders];
    }

    return generatedHeaders;
  };

  handleSort = (sortValue: string, sortDirection: SortDirection) => {
    this.setState({ sortColumn: sortValue, sortDirection });

    if (this.props.handleSort) {
      this.props.handleSort(sortValue, sortDirection);
    }
  };

  buildRow = (row: T, index: number): JSX.Element[] => {
    const generatedRow = this.props.columnGenerators.map((generator, i) => {
      const key = i;
      return <td key={key}>{generator(row)}</td>;
    });

    if (this.props.spreadsheet) {
      // add a cell at the beginning of the row with the
      // row number
      const rowNumberCell = (
        <td key="line-number">
          <Text color="wolf">{index}</Text>
        </td>
      );
      return [rowNumberCell, ...generatedRow];
    }
    return generatedRow;
  };

  renderTableBody = (keyField: Field<T>, rows: T[]): React.ReactElement[] => {
    const { handleRowClick, keysOfActiveRows } = this.props;

    const activeRowsKeyHash = keyBy(keysOfActiveRows, key => key);

    return rows.map((row, index) => {
      const key = getField(row, keyField);
      const canHandleRowClick = !!handleRowClick || Boolean(('onClick' in row) )
      return (
        <TableRow
          hoverable={canHandleRowClick}
          onClick={() => {
            if (handleRowClick) {
              handleRowClick(row);
            }
            if ('onClick' in row) {
              // this is not ideal, but it stops us from having to do subclassing in everything that uses composable table.
              // otherwise each row type would have to extend "GenericrRowInterface"
              (row as any).onClick();
            }
          }}
          key={key}
          active={key in activeRowsKeyHash}>
          {this.buildRow(row, index)}
        </TableRow>
      );
    });
  };

  render() {
    const {
      actionsColumn,
      className,
      columnGenerators,
      columnHeaders,
      keyField,
      handleSort,
      rows,
      fixedHeader,
      loading,
    } = this.props;

    if (columnHeaders) {
      invariant(
        columnGenerators.length === columnHeaders.length,
        "Won't have the same number of headers as columns!"
      );
      invariant(
        columnHeaders.some(header => !!header.sortValue) ? handleSort : true,
        'Missing sortable function for sorting columns'
      );
    }

    return (
      <Table
        actionsColumn={actionsColumn}
        fixedHeader={fixedHeader}
        head={columnHeaders ? <tr>{this.generateHeaders(columnHeaders)}</tr> : null}
        loading={loading ?? !rows}
        className={cx('ComposableTable', className)}>
        <tbody>{rows && this.renderTableBody(keyField, rows)}</tbody>
      </Table>
    );
  }
}

export default ComposableTable;
